import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ConfigurationService } from './configuration.service';
import { EndpointFactory } from './endpoint-factory.service';
import { KappaForm } from '../models/report/kappa-form.model';
import { SubmissionType } from '../models/enums';


@Injectable({
  providedIn: 'root'
})
export class ReportEndpoint extends EndpointFactory {

  private readonly _baseUrl = this.configurations.baseUrl + '/api/report/';

  get kappaUrl() { return this._baseUrl + 'kappa'; }
  get abstractSummaryUrl() { return this._baseUrl + 'summary'; }
  get abstractValuesUrl() { return this._baseUrl + 'values'; }
  get weeklyCoderUrl() { return this._baseUrl + 'coder'; }
  get weeklyCoderConsensusUrl() { return this._baseUrl + 'consensus'; }
  get recCompletedUrl() { return this._baseUrl + 'reccompleted'; }
  get abstractFysUrl() { return this._baseUrl + 'fy'; }
  get qcDatesUrl() { return this._baseUrl + 'dates'; }
  get qcDatesWithStartUrl() { return this._baseUrl + 'dateswithstart'; }
  get qcStEndDatesUrl() { return this._baseUrl + 'qcstenddts'; }
  get mechanismTypesUrl() { return this._baseUrl + 'mechanisms'; }

  get lastKappaDateUrl() { return this._baseUrl + 'lastkappadate'; }

  constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
    super(http, configurations, injector);
  }

  getKappaReportEndpoint<T>(kappaForm: KappaForm): Observable<T> {
    const options = this.getRequestHeaders();
    options['responseType'] = 'arraybuffer';

    return this.http.post<T>(this.kappaUrl, kappaForm, options).pipe(
      catchError(error => {
        if (error.error instanceof ArrayBuffer) { error.error = this.DecodeArrayBuffer(error.error); }
        return throwError(error);
    }));
  }

  getAbstractSummaryReportEndpoint<T>(odpProjectWorkflowId: number): Observable<T> {
    const options = this.getRequestHeaders();
    if (odpProjectWorkflowId) { options['params'] = { odpProjectWorkflowId }; }
    options['responseType'] = 'arraybuffer';

    return this.http.post<T>(this.abstractSummaryUrl, {}, options).pipe(
      catchError(error => {
        if (error.error instanceof ArrayBuffer) { error.error = this.DecodeArrayBuffer(error.error); }
        return throwError(error);
    }));
  }

  getAbstractValuesReportEndpoint<T>(odpProjectWorkflowId: number, year: string): Observable<T> {
    const options = this.getRequestHeaders();
    if (year) { options['params'] = { odpProjectWorkflowId, year }; } else { options['params'] = { odpProjectWorkflowId}; }
    options['responseType'] = 'arraybuffer';

    return this.http.post<T>(this.abstractValuesUrl, {}, options).pipe(
      catchError(error => {
        if (error.error instanceof ArrayBuffer) { error.error = this.DecodeArrayBuffer(error.error); }
        return throwError(error);
    }));
  }

  getWeeklyCoderReportEndpoint<T>(odpProjectWorkflowId: number, startDate: string, endDate: string): Observable<T> {
    const options = this.getRequestHeaders();
    options['params'] = { odpProjectWorkflowId, startDate, endDate };
    options['responseType'] = 'arraybuffer';

    return this.http.post<T>(this.weeklyCoderUrl, {}, options).pipe(
      catchError(error => {
        if (error.error instanceof ArrayBuffer) { error.error = this.DecodeArrayBuffer(error.error); }
        return throwError(() => error);
    }));
  }

  getWeeklyCoderConsensusReportEndpoint<T>(odpProjectWorkflowId: number, startDate: string, endDate: string, submissionType: SubmissionType): Observable<T> {
    const options = this.getRequestHeaders();
    options['params'] = { odpProjectWorkflowId, startDate, endDate, submissionType };
    options['responseType'] = 'arraybuffer';

    return this.http.post<T>(this.weeklyCoderConsensusUrl, {}, options).pipe(
      catchError(error => {
        if (error.error instanceof ArrayBuffer) { error.error = this.DecodeArrayBuffer(error.error); }
        return throwError(() => error);
    }));
  }

  getRecCompletedReportEndpoint<T>(odpProjectWorkflowId: number, startDate: string, endDate: string): Observable<T> {
    const options = this.getRequestHeaders();
    options['params'] = { odpProjectWorkflowId, startDate, endDate };
    options['responseType'] = 'arraybuffer';

    return this.http.post<T>(this.recCompletedUrl, {}, options).pipe(
      catchError(error => {
        if (error.error instanceof ArrayBuffer) { error.error = this.DecodeArrayBuffer(error.error); }
        return throwError(() => error);
      }));
  }

  getAbstractFYsEndpoint<T>(odpProjectWorkflowId: number): Observable<T> {
    const options = this.getRequestHeaders();
    options['params'] = { odpProjectWorkflowId };
    return this.http.get<T>(this.abstractFysUrl, options);
  }

  getQcDatesEndpoint<T>(): Observable<T> {
    return this.http.get<T>(this.qcDatesUrl, this.getRequestHeaders());
  }

  getQcDatesWithStartEndpoint<T>(startDate: string): Observable<T> {
    const options = this.getRequestHeaders();
    options['params'] = { startDate };

    return this.http.post<T>(this.qcDatesWithStartUrl, {}, options);
  }

  getQcWeeksStEndDatesEndpoint<T>(): Observable<T> {
    return this.http.get<T>(this.qcStEndDatesUrl, this.getRequestHeaders());
  }

  getMechanismTypesEndpoint<T>(odpProjectWorkflowId: number): Observable<T> {
    const options = this.getRequestHeaders();
    options['params'] = { odpProjectWorkflowId };
    return this.http.get<T>(this.mechanismTypesUrl, options);
  }

  /*getLastKappaReportDatEndpoint<T>(odpProjectWorkflowId: number): Observable<T> {
    const options = this.getRequestHeaders();
    options['params'] = { odpProjectWorkflowId };

    return this.http.get<T>(this.lastKappaDateUrl, options);
  }*/

  getLastKappaReportDateEndpoint(odpProjectWorkflowId: number): Observable<string> {
    const options = this.getRequestHeaders();
    options['params'] = { odpProjectWorkflowId };
    options['responseType'] = 'text' as const;

    /*return this.http.get(this.lastKappaDateUrl, {
      headers,
      params: { odpProjectWorkflowId },
      responseType: 'text' as const
    });*/
    

    return this.http.get<string>(this.lastKappaDateUrl, options);
  }
}
